import React, { useRef } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  Code,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useExpireLinkMutation } from '../../store/linksApi';
import type { Link } from '../../types/link';

interface Props {
  link: Link | null;
  onClose: () => void;
}

export const ExpireLinkDialog: React.FC<Props> = ({ link, onClose }) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [expireLink, { isLoading }] = useExpireLinkMutation();
  const toast = useToast();

  const handleExpire = async () => {
    if (!link) return;
    try {
      await expireLink(link.id).unwrap();
      toast({ title: `Disabled ${link.shortCode}`, status: 'success', duration: 3000, isClosable: true });
      onClose();
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? 'Failed to disable link';
      toast({ title: 'Could not disable link', description: msg, status: 'error', isClosable: true });
    }
  };

  return (
    <AlertDialog isOpen={!!link} leastDestructiveRef={cancelRef} onClose={onClose} isCentered>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Disable short link
          </AlertDialogHeader>
          <AlertDialogBody>
            <Text>
              Disable <Code>{link?.shortCode}</Code>? It will stop redirecting and return the
              expired page. Existing analytics are kept. This cannot be undone in v1 (links are
              immutable post-mint).
            </Text>
          </AlertDialogBody>
          <AlertDialogFooter gap={3}>
            <Button ref={cancelRef} variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={handleExpire} isLoading={isLoading} loadingText="Disabling">
              Disable link
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};
